import cn from "classnames";
import { useTranslation } from "react-i18next";

import { LoansProps, LoansFieldsProps } from "./Loans.types";

import { Typography } from "shared/ui/components/Typography";

import { InputField } from "components/FormikFields";
import { CalculatorFormikField } from "components/CalculatorFormikField";

import styles from "./Loans.module.scss";

const Fields = ({
  t,
  onLoanYearsChange,
  values,
  isRootAdmin,
}: LoansFieldsProps) => {
  return (
    <div className={styles.fields}>
      <div className={styles.fieldsColumn}>
        <div className={cn(styles.header)}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Allgemeine Parameter")}
          </Typography>
        </div>
        <CalculatorFormikField
          name={`client.loanYears`}
          component={InputField}
          controlProps={{
            label: t("calculator_form.loans.loan_years"),
            variant: "horizontal",
            className: styles.fieldControl,
          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: (
              <Typography className={styles.currency} variant="bodyM" weight="medium">
                {t("calculator_form.loans.years")}
              </Typography>
            ),
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper,
            onChanged: (_: any, value: any) => {
              onLoanYearsChange(value);
            },
          }}
        />
        {isRootAdmin && (
          <CalculatorFormikField
            name={`client.capital`}
            component={InputField}
            controlProps={{
              label: t("calculator_form.loans.equity_capital"),
              variant: "horizontal",
              className: styles.fieldControl,
            }}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper,
            }}
          />
        )}
        <div className={cn(styles.header)}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Bankdarlehen")}
          </Typography>
        </div>
        <CalculatorFormikField
          name={`loans[0].startRate`}
          component={InputField}
          controlProps={{
            required: true,
            label: t("calculator_form.loans.standard_loan_1st_interest"),
            variant: "horizontal",
            className: styles.fieldControl,
          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper,
          }}
        />
        <CalculatorFormikField
          name={`loans[0].rate`}
          component={InputField}
          controlProps={{
            required: true,
            label: t("calculator_form.loans.standard_loan_2nd_interest"),
            variant: "horizontal",
            className: styles.fieldControl,

          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper,
          }}
        />
      </div>
      {(values.energyEfficiencyClass?.value === "KW" ||
        values.energyEfficiencyClass?.value === "QNG") && (
          <>
            <div className={cn(styles.header)}>
              <Typography
                className={styles.headerName}
                variant="bodyS"
                component="span"
              >
                {t("EH40 mit QNG KfW-Darlehen")}
              </Typography>
            </div>
            <div className={styles.fieldsColumn}>
              <CalculatorFormikField
                name={`loans[1].startRate`}
                component={InputField}
                controlProps={{
                  required: true,
                  variant: "horizontal",
                  label: `${values.loans?.[1]?.name} ${t(
                    "calculator_form.loans.kfw_loan_1st_interest"
                  )}`,
                  className: styles.fieldControl,
                }}
                componentProps={{
                  type: "localeDe",
                  placeholder: "",
                  disabled: true,
                  endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
                  withoutBorder: true,
                  fieldClassName: styles.input,
                  className: styles.inputWrapper,
                }}
              />
              <CalculatorFormikField
                name={`loans[1].rate`}
                component={InputField}
                controlProps={{
                  required: true,
                  variant: "horizontal",
                  label: `${values.loans?.[1]?.name} ${t(
                    "calculator_form.loans.kfw_loan_2nd_interest"
                  )}`,
                  className: styles.fieldControl,
                }}
                componentProps={{
                  type: "localeDe",
                  placeholder: "",
                  endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
                  withoutBorder: true,
                  fieldClassName: styles.input,
                  className: styles.inputWrapper,
                }}
              />
            </div>
          </>

        )}
    </div>
  );
};

const LoansDesktop = ({
  t,
  values,
  onLoanYearsChange,
  isRootAdmin,
}: LoansFieldsProps) => {
  return (
    <div className={styles.rootDesktop}>
      <Fields
        t={t}
        values={values}
        onLoanYearsChange={onLoanYearsChange}
        isRootAdmin={isRootAdmin}
      />
    </div>
  );
};

const LoansMobile = ({
  t,
  values,
  onLoanYearsChange,
  isRootAdmin,
}: LoansFieldsProps) => {
  return (
    <div className={styles.rootMobile}>
      <Fields
        t={t}
        values={values}
        onLoanYearsChange={onLoanYearsChange}
        isRootAdmin={isRootAdmin}
      />
    </div>
  );
};

const Loans = ({
  values,
  activeTab,
  isDesktop,
  isRootAdmin,
  onLoanYearsChange,
}: LoansProps) => {
  const { t } = useTranslation();

  if (activeTab !== "loans") {
    return null;
  }

  if (!isDesktop) {
    return (
      <LoansMobile
        t={t}
        values={values}
        onLoanYearsChange={onLoanYearsChange}
        isRootAdmin={isRootAdmin}
      />
    );
  }

  return (
    <LoansDesktop
      t={t}
      values={values}
      onLoanYearsChange={onLoanYearsChange}
      isRootAdmin={isRootAdmin}
    />
  );
};

export default Loans;
